import { FileUploadStatus } from "@portal/api/files";
import type { FC } from "react";
import {
	ClassificationAwaitingReviewIndicator,
	ClassificationFailedIndicator,
	ClassificationPendingIndicator,
} from "../../patterns/classification-status";
import { Icon, Spinner } from "../../primitives";
import { PortaledTooltip } from "../../ui/Tooltip";

type FileUploadStatusIndicatorProps = {
	status: FileUploadStatus;
	/** Stable id for tooltips (row/file id). */
	id: string;
};

const UPLOADING_TOOLTIP = "Uploading...";
const UPLOAD_FAILED_TOOLTIP = "Upload failed. Remove the file and try again.";

const UploadingIndicator: FC<{ id: string }> = ({ id }) => {
	const tooltipId = `file-upload-uploading-${id}`;
	return (
		<>
			<span
				data-tooltip-id={tooltipId}
				data-tooltip-content={UPLOADING_TOOLTIP}
				aria-label={UPLOADING_TOOLTIP}
				data-testid="upload-pending-indicator"
				className="inline-flex items-center"
			>
				<Spinner size="sm" className="text-secondary" />
			</span>
			<PortaledTooltip id={tooltipId} />
		</>
	);
};

/**
 * Single status chrome for upload + classification. Pair with `resolveFileUploadStatus`.
 */
export const FileUploadStatusIndicator: FC<FileUploadStatusIndicatorProps> = ({ status, id }) => {
	switch (status) {
		case FileUploadStatus.uploading:
			return <UploadingIndicator id={id} />;
		case FileUploadStatus.classifying:
			return <ClassificationPendingIndicator rowId={id} />;
		case FileUploadStatus.awaitingReview:
			return <ClassificationAwaitingReviewIndicator rowId={id} />;
		case FileUploadStatus.classificationFailed:
			return <ClassificationFailedIndicator rowId={id} />;
		case FileUploadStatus.uploadFailed:
			return (
				<span
					aria-label={UPLOAD_FAILED_TOOLTIP}
					title={UPLOAD_FAILED_TOOLTIP}
					data-testid="upload-failed-indicator"
					className="inline-flex items-center"
				>
					<Icon type="exclamation-circle" color="danger" size="mini" />
				</span>
			);
		case FileUploadStatus.ready:
			return null;
	}
};
